import type {
  PortalClientFile,
  PortalRequestResponse,
} from "@ledgerbox/domain"

/* History lists every file the client sent (R12-U1): the files on each
   request's items first, grouped by request, then the rest. Plain logic, so
   the grouping can be tested without a browser. */

export type HistoryGroup = {
  key: string
  /** The request's name, or null for files on no request still open. */
  title: string | null
  rows: { file: PortalClientFile; itemName: string | null }[]
}

export const historyGroups = (
  files: readonly PortalClientFile[],
  requests: readonly PortalRequestResponse[],
  requestName: (request: PortalRequestResponse) => string
): { onRequests: HistoryGroup[]; loose: PortalClientFile[] } => {
  const placed = new Set<string>()
  const onRequests: HistoryGroup[] = []
  for (const request of requests) {
    const rows = request.items.flatMap((item) =>
      files
        .filter((file) => file.requestItemId === item.itemId)
        .map((file) => ({ file, itemName: item.name }))
    )
    for (const row of rows) placed.add(row.file.fileId)
    if (rows.length)
      onRequests.push({
        key: request.requestId,
        title: requestName(request),
        rows,
      })
  }
  // A file on an item of a request that is no longer open.
  const earlier = files.filter(
    (file) => file.requestItemId !== null && !placed.has(file.fileId)
  )
  if (earlier.length)
    onRequests.push({
      key: "earlier",
      title: null,
      rows: earlier.map((file) => ({ file, itemName: null })),
    })
  return {
    onRequests,
    loose: files.filter((file) => file.requestItemId === null),
  }
}
